import type {
  ApprovalMode,
  ActionApprovalRule as StoredActionApprovalRule,
} from "@rakazo/contracts";
import { commandWatchOf } from "@rakazo/contracts";
import { sitesInText } from "./egress.js";

const APPROVAL_EXEMPT_TOOLS = new Set([
  "computer_observe",
  "computer_act",
  "browser_navigate",
  "browser_snapshot",
  "browser_act",
  "list_files",
  "list_artifacts",
  "get_artifact",
  "read_file",
  "read_history",
  "search_history",
  "write_file",
  "shell",
  "open_path",
  "launch_app",
  "remember",
  "report_restricted_request",
  "request_takeover",
  "request_secret",
  "ask_text",
  "ask_file",
  "run_subagent",
  "spawn_bot",
  "schedule_create",
  "schedule_list",
  "schedule_cancel",
  "list_fleet",
  "organize_bot",
]);

const APPROVAL_REQUIRED_BUILTIN_TOOLS = new Set([
  "site_publish",
  "generate_image",
  "generate_video",
  "delete_bot",
  "archive_bot",
  "secret_request",
  "forget_secret",
  "forget_memory",
  "cloud_agent_launch",
  "cloud_agent_reply",
  "cloud_agent_cancel",
]);
const EXPLICIT_APPROVAL_BUILTIN_TOOLS = new Set([
  "create_space",
  "propose_instructions_update",
  "create_group",
  "update_group",
  "set_child_instructions",
  "set_child_bot_model",
  "create_child_routine",
  // Accepting a connection lets another owner's agent write to this owner's bots, and the tool
  // is described to the model as "only on the owner's explicit instruction". A model reading
  // injected text must not be able to take that decision itself.
  "respond_agent_connection",
]);

const UNATTENDED_SAFE_BUILTIN_TOOLS = new Set([
  "connect_devices",
  "connect_result",
  "site_schema",
  "site_list",
  "site_versions",
  "site_source",
  "list_artifacts",
  "get_artifact",
  "browser_snapshot",
  "cloud_agent_status",
  "computer_observe",
  "list_files",
  "list_fleet",
  "list_secrets",
  "read_file",
  "recall_memory",
  "report_restricted_request",
  "request_takeover",
  "run_subagent",
  "task_catalog",
  "schedule_list",
  "scratchpad_list",
  "skill_read",
  "web_fetch",
  "web_search",
]);

const READ_ONLY_CONNECTOR_PATTERN = /(^|_)(get|list|search|find|read)(_|$)/i;
const MUTATING_CONNECTOR_PATTERN =
  /(^|_)(accept|add|approve|archive|assign|buy|cancel|charge|checkout|close|commit|copy|create|delete|deploy|disable|enable|execute|forward|grant|invite|link|mark|merge|modify|move|patch|pay|post|publish|purchase|put|register|reject|remove|rename|replace|reply|reset|revoke|run|schedule|send|set|share|start|stop|submit|subscribe|trigger|unassign|unlink|unsubscribe|update|upload|upsert|write)(_|$)/i;
const COMPOUND_CONNECTOR_ACTION_PATTERN = /_(and|or|then)_/i;

const EMAIL_CONNECTOR_SLUGS = new Set(["gmail", "outlook", "microsoft_outlook"]);
const PURCHASE_CONNECTOR_SLUGS = new Set(["stripe", "shopify", "paypal", "square"]);

export type ActionApprovalRule = Pick<
  StoredActionApprovalRule,
  "effect" | "matchKind" | "matchValue"
>;

export function connectorKindFromToolName(toolName: string, connectorKinds: string[] = []): string {
  const normalizedTool = toolName.toLowerCase();
  const matched = connectorKinds
    .map((kind) => kind.toLowerCase())
    .filter((kind) => normalizedTool === kind || normalizedTool.startsWith(`${kind}_`))
    .sort((left, right) => right.length - left.length)[0];
  if (matched) return matched;
  const [segment] = toolName.split("_");
  return (segment ?? toolName).toLowerCase();
}

/**
 * An MCP tool is called `mcp__<server>__<tool>` and whoever adds the server names it, so a
 * server called "search" must not make its tools look like reads. Only the part after the last
 * double underscore is the tool's own name.
 */
function connectorActionName(toolName: string): string {
  const prefix = "mcp__";
  if (!toolName.startsWith(prefix)) return toolName;
  const separator = toolName.lastIndexOf("__");
  return separator > prefix.length ? toolName.slice(separator + 2) : toolName;
}

/**
 * `readOnly` is the connector tool's declared effect (for installed APIs, derived from the stored
 * HTTP method and read-only flag). A declared write always needs approval. A declared read never
 * relaxes the name check: the provider chooses both the name and the hint.
 */
export function connectorToolRequiresApproval(toolName: string, readOnly?: boolean): boolean {
  if (readOnly === false) return true;
  if (MUTATING_CONNECTOR_PATTERN.test(toolName)) return true;
  if (COMPOUND_CONNECTOR_ACTION_PATTERN.test(toolName)) return true;
  return !READ_ONLY_CONNECTOR_PATTERN.test(connectorActionName(toolName));
}

/** Unattended watches require explicit read-only metadata and must respect approval rules. */
export function watchToolRefusal(input: {
  toolName: string;
  readOnly?: boolean;
  connectorKind?: string;
  rules: ActionApprovalRule[];
}): string | null {
  const { toolName, readOnly } = input;
  if (readOnly !== true) {
    return `${toolName} can change data, so a change trigger can't run it. Pick a read-only tool.`;
  }
  if (
    resolveActionApprovalDetail({
      toolName,
      connectorKind: input.connectorKind,
      readOnly,
      rules: input.rules,
    }).decision === "ask"
  ) {
    return `${toolName} needs approval under this space's rules, and a change check cannot ask.`;
  }
  return null;
}

export function toolRequiresApproval(
  toolName: string,
  viaConnector: boolean,
  readOnly?: boolean,
): boolean {
  if (APPROVAL_EXEMPT_TOOLS.has(toolName)) return false;
  if (toolRequiresExplicitApproval(toolName)) return true;
  if (APPROVAL_REQUIRED_BUILTIN_TOOLS.has(toolName)) return true;
  if (viaConnector) return connectorToolRequiresApproval(toolName, readOnly);
  return false;
}

/** Security-boundary changes cannot be auto-reviewed or permanently allowed. */
export function toolRequiresExplicitApproval(toolName: string): boolean {
  return EXPLICIT_APPROVAL_BUILTIN_TOOLS.has(toolName);
}

/**
 * A run the owner started: their own message, a follow-up they wrote, or the close of their call.
 * Bot messages, channels, spawns, tickets, webhooks and routines are not, nor is anything new.
 */
export function isOwnerStartedRun(trigger: string, followUpFromUser: boolean): boolean {
  if (trigger === "follow_up") return followUpFromUser;
  return trigger === "user" || trigger === "call_end";
}

/** External webhook runs may inspect state unattended, but side effects always need the owner. */
export function unattendedTriggerToolRequiresApproval(
  trigger: string,
  toolName: string,
  viaConnector: boolean,
  readOnly?: boolean,
): boolean {
  if (trigger !== "webhook") return false;
  return viaConnector
    ? connectorToolRequiresApproval(toolName, readOnly)
    : !UNATTENDED_SAFE_BUILTIN_TOOLS.has(toolName);
}

/**
 * Tools whose results bring in nothing from outside during the run: confirmations of the bot's own
 * writes, the owner's answers, and the product's own saved state (memory, skills, history), which
 * is treated like the conversation already in context. Every other result (pages, search hits,
 * files, connector data, the screen, shell output, other bots) can carry text an attacker wrote,
 * and taints the run. New tools taint by default.
 */
const OWN_STATE_RESULT_TOOLS = new Set([
  "ask_user",
  "ask_text",
  "connect_app",
  "attach_file",
  "attach_artifact",
  "end_call",
  "list_fleet",
  "list_secrets",
  "message_user",
  "read_history",
  "recall_memory",
  "remember",
  "render_plot",
  "report_restricted_request",
  "request_secret",
  "save_memory",
  "save_shared_memory",
  "schedule_cancel",
  "schedule_create",
  "schedule_list",
  "scratchpad_add",
  "scratchpad_complete",
  "scratchpad_list",
  "scratchpad_remove",
  "scratchpad_update",
  "search_history",
  "skill_read",
  "task_catalog",
  "write_file",
]);

export function toolResultTaintsRun(toolName: string): boolean {
  return !OWN_STATE_RESULT_TOOLS.has(toolName);
}

/**
 * A connector read still sends its arguments to the connector's server. Until outside content
 * enters the run, those arguments come from the owner's request. After that, injected text could
 * choose them, so a read skips approval only on a connector this run already used before then or
 * was approved for. Reads on that connector stay free, so list-then-get flows never ask twice.
 */
export function connectorReadRequiresApproval(input: {
  tainted: boolean;
  connectorTrusted: boolean;
}): boolean {
  return input.tainted && !input.connectorTrusted;
}

/**
 * web_fetch sends its whole URL, query string included, to the site it names, so a site this run
 * was not given is a new destination that injected text could have picked. Trusted sites (named in
 * the owner's own words, approved earlier in the run, or the exact URL of a search result) and
 * sites with an always-allow rule go through. Anything else is new: planActionGate decides who
 * judges it, and newSiteOutcome what happens when nobody passed it.
 */
export function outboundSiteGate(input: {
  site: string;
  trusted: boolean;
  rules: ActionApprovalRule[];
}): "allow" | "new" {
  return input.trusted || siteAlwaysAllowed(input.rules, input.site) ? "allow" : "new";
}

/**
 * A new site that Auto Review did not pass needs a person. A webhook run has nobody to ask, so
 * there it is refused.
 */
export function newSiteOutcome(input: {
  trigger: string;
  decision: "ask" | "allow";
}): "ask" | "allow" | "deny" {
  if (input.decision === "allow") return "allow";
  return input.trigger === "webhook" ? "deny" : "ask";
}

/**
 * A site rule for every site: the owner lets bots read any website without asking, accepting that
 * text injected into a run could then pick where a fetch goes.
 */
export const ANY_SITE = "*";

export function siteAlwaysAllowed(rules: ActionApprovalRule[], site: string): boolean {
  return rules.some(
    (rule) =>
      rule.effect === "always_allow" &&
      rule.matchKind === "site" &&
      (rule.matchValue === ANY_SITE || rule.matchValue.toLowerCase() === site),
  );
}

function categoryMatches(
  category: string,
  toolName: string,
  connectorKind: string,
  readOnly: boolean | undefined,
): boolean {
  const normalized = category.toLowerCase();
  const consequential = connectorToolRequiresApproval(toolName, readOnly);
  if (normalized === "email") {
    if (EMAIL_CONNECTOR_SLUGS.has(connectorKind.toLowerCase())) return consequential;
    return consequential && /send.*mail|gmail_send|outlook_send/i.test(toolName);
  }
  if (normalized === "purchase") {
    if (PURCHASE_CONNECTOR_SLUGS.has(connectorKind.toLowerCase())) return consequential;
    return consequential && /purchase|pay_|charge|checkout|buy_/i.test(toolName);
  }
  return false;
}

function ruleMatches(
  rule: ActionApprovalRule,
  toolName: string,
  connectorKind: string,
  readOnly: boolean | undefined,
  site: string | undefined,
): boolean {
  const value = rule.matchValue.toLowerCase();
  switch (rule.matchKind) {
    case "site":
      return site === value;
    case "tool":
      return toolName.toLowerCase() === value;
    case "connector":
      return connectorKind.toLowerCase() === value;
    case "category":
      return categoryMatches(rule.matchValue, toolName, connectorKind, readOnly);
    default:
      return false;
  }
}

function ruleSpecificity(rule: ActionApprovalRule): number {
  switch (rule.matchKind) {
    case "site":
      return 4;
    case "tool":
      return 3;
    case "connector":
      return 2;
    case "category":
      return 1;
    default:
      return 0;
  }
}

export type ActionApprovalSource = "require_approval" | "always_allow" | "default";

export type ActionApprovalResolved = {
  decision: "ask" | "allow";
  source: ActionApprovalSource;
  matchingRules: ActionApprovalRule[];
};

/**
 * Deterministic rule resolution. Always-allow and require-approval both beat the default.
 * `site` is the destination of an outbound fetch; a rule for that site beats a rule for the tool.
 */
export function resolveActionApprovalDetail(input: {
  toolName: string;
  connectorKind?: string;
  readOnly?: boolean;
  site?: string;
  rules: ActionApprovalRule[];
}): ActionApprovalResolved {
  const connectorKind = input.connectorKind ?? connectorKindFromToolName(input.toolName);
  const matchingRules = input.rules.filter((rule) =>
    ruleMatches(rule, input.toolName, connectorKind, input.readOnly, input.site),
  );
  if (matchingRules.length === 0) {
    return { decision: "allow", source: "default", matchingRules };
  }

  const highestSpecificity = Math.max(...matchingRules.map(ruleSpecificity));
  const winners = matchingRules.filter((rule) => ruleSpecificity(rule) === highestSpecificity);
  if (winners.some((rule) => rule.effect === "require_approval")) {
    return { decision: "ask", source: "require_approval", matchingRules };
  }
  return { decision: "allow", source: "always_allow", matchingRules };
}

export function resolveActionApproval(input: {
  toolName: string;
  connectorKind?: string;
  readOnly?: boolean;
  rules: ActionApprovalRule[];
}): "ask" | "allow" {
  return resolveActionApprovalDetail(input).decision;
}

export type AutoReviewJudgeDecision = "pass" | "ask" | "error";

/**
 * Pure combiner for Auto Review after rule resolution.
 * Rules win: require_approval asks, always_allow runs. Only the default path may call a judge.
 * The judge only escalates to ask; it never silent-denies.
 */
export function planActionGate(input: {
  resolved: ActionApprovalResolved;
  consequential: boolean;
  autoReviewEnabled: boolean;
  checkerConfigured: boolean;
  /** A fetch to a site the run was not given (see outboundSiteGate). */
  newDestination?: boolean;
  /** The space's approval mode, `manual` when absent. */
  mode?: ApprovalMode;
  trigger?: string;
  /** Text from outside entered this run after it began (see approvalGateOff). */
  receivedOutsideMessage?: boolean;
  /** Outside text started this run (see runStartedByOutsideText). */
  startedByOutsideText?: boolean;
  /**
   * Whether the reviewer has the owner's own words for this run. A run someone else started has
   * only that someone's prompt, which the reviewer must not take for the owner's task, so with no
   * owner words it is a person who decides. Absent means the owner started the run.
   */
  judgeHasOwnerWords?: boolean;
}): "ask" | "allow" | "judge" {
  // Mode "none" comes first and overrides everything below it: the tools that always need a
  // person, the owner's own require_approval rules, new sites and the reviewer. The owner chose
  // that; approvalGateOff names the runs it still does not reach.
  if (
    approvalGateOff({
      mode: input.mode ?? "manual",
      trigger: input.trigger ?? "",
      receivedOutsideMessage: input.receivedOutsideMessage,
      startedByOutsideText: input.startedByOutsideText,
    })
  ) {
    return "allow";
  }
  if (input.resolved.decision === "ask") return "ask";
  // A rule for the whole tool is not a choice of sites, so it never allows a new one; only a site
  // rule does, and that makes the site no longer new. Auto Review may pass one, judging only the
  // owner's own words and the full URL: it never sees tool results or inbound messages, so text
  // injected there cannot argue for the site. Without Auto Review a person decides.
  if (input.newDestination) {
    return input.autoReviewEnabled && input.checkerConfigured ? "judge" : "ask";
  }
  if (input.resolved.source === "always_allow") return "allow";
  if (
    input.consequential &&
    input.autoReviewEnabled &&
    input.checkerConfigured &&
    input.resolved.source === "default"
  ) {
    return input.judgeHasOwnerWords === false ? "ask" : "judge";
  }
  return "allow";
}

/**
 * Mode "none" ends approval gating, with exceptions that keep a person in the loop wherever
 * someone else's text could be steering the bot: a run a webhook started (its payload is
 * attacker-controlled and nobody is there to ask, so it keeps asking or refusing as before), a run
 * that outside text started (a group channel or Team Chat message, see runStartedByOutsideText),
 * and a run that took in a channel or webhook message after it began. Everything else that needs a
 * human by nature (a secret, a takeover, an OAuth connection, a question) is not an approval and
 * is not decided here.
 */
export function approvalGateOff(input: {
  mode: ApprovalMode;
  trigger: string;
  receivedOutsideMessage?: boolean;
  startedByOutsideText?: boolean;
}): boolean {
  return (
    input.mode === "none" &&
    input.trigger !== "webhook" &&
    !input.receivedOutsideMessage &&
    !input.startedByOutsideText
  );
}

/**
 * The variables a shell command would receive that are worth a question: every secret the run
 * can hand a command, once the run has read outside content, because that content may have
 * planted the command and nothing stops it sending them anywhere. A clean run, a run a person
 * already allowed this for, and a bot with nothing to hand over need no question.
 */
export function taintedShellSecrets(input: {
  tainted: boolean;
  /** A person already allowed commands to use them in this run. */
  trusted: boolean;
  variableNames: readonly string[];
}): string[] {
  if (!input.tainted || input.trusted) return [];
  return [...input.variableNames].sort();
}

/**
 * What a run writes that outlives it and reaches other bots: the Space's shared document, the
 * skills every bot of the owner is told to follow, and a new bot. After the run read outside
 * content these ask, because that content may be what is being saved. The bot's own notes
 * (`remember`) are frequent and stay with that bot, so they are not here.
 */
const TAINTED_PERSISTENCE_TOOLS = new Set([
  "save_shared_memory",
  "skill_create",
  "skill_update",
  "skill_delete",
  "spawn_bot",
]);

export function taintedPersistenceRequiresApproval(toolName: string): boolean {
  return TAINTED_PERSISTENCE_TOOLS.has(toolName);
}

/** Whether a stored shell request is one a person allowed to use secrets (see taintedShellSecrets). */
export function isShellSecretsApproval(request: unknown): boolean {
  return (
    typeof request === "object" &&
    request !== null &&
    "withSecrets" in request &&
    Array.isArray(request.withSecrets)
  );
}

/** Team Chat puts this on the message that starts a run (`teamchat:<provider>:<event>`). */
const TEAM_CHAT_RUN_NONCE_PREFIX = "teamchat:";

/**
 * Whether the text that started a messaging run may be someone other than the owner. A group
 * channel on the shared line is (it carries a channel_message block), and so is any Team Chat
 * message, because Team Chat does not check who in the conversation wrote it. The owner's own
 * verified direct line is not, so choosing "none" still holds there.
 */
export function runStartedByOutsideText(input: {
  trigger: string;
  /** isMessagingChannelRun for this run. */
  channelRun: boolean;
  /** The client nonce of the message that woke the run. */
  clientNonce?: string | null;
}): boolean {
  if (input.trigger !== "messaging") return false;
  return input.channelRun || Boolean(input.clientNonce?.startsWith(TEAM_CHAT_RUN_NONCE_PREFIX));
}

/**
 * The mode a stored preference means. `enabled` is the column the previous release wrote and still
 * reads: true only for Auto Review. Only the exact stored value "none", with `enabled` false,
 * reads as none; a row the previous release changed afterwards, or any other value, follows
 * `enabled`, which is never the less careful reading. With no row the deployment default applies.
 */
export function effectiveApprovalMode(
  row: { enabled: boolean; approvalMode?: string | null } | null | undefined,
  deploymentDefaultAuto: boolean,
): ApprovalMode {
  if (!row) return deploymentDefaultAuto ? "auto" : "manual";
  if (row.approvalMode === "none" && !row.enabled) return "none";
  return row.enabled ? "auto" : "manual";
}

/** How many sites one "always" answer on a routine's card may allow. */
export const ROUTINE_ALWAYS_SITES_MAX = 3;
/** Longest list of sites that is shown whole on the button. */
const ROUTINE_ALWAYS_SITES_MAX_CHARS = 120;

/**
 * The sites an "always" answer on a routine's approval card allows: the sites its prompt names,
 * when there are one to three and the list reads whole on the button. Never for a routine that
 * watches with a command, which runs unattended shell work, and never in a webhook run, whose
 * payload someone else wrote. The card is built from this and the server works the list out again
 * from the stored routine when the answer comes in, so the two always agree.
 */
export function routineAlwaysSites(input: {
  prompt: unknown;
  watch?: unknown;
  trigger: string;
}): string[] {
  if (input.trigger === "webhook" || commandWatchOf(input.watch) !== undefined) return [];
  if (typeof input.prompt !== "string") return [];
  const sites = [...sitesInText(input.prompt)].sort();
  if (sites.length < 1 || sites.length > ROUTINE_ALWAYS_SITES_MAX) return [];
  return sites.join(", ").length > ROUTINE_ALWAYS_SITES_MAX_CHARS ? [] : sites;
}

/** Map a judge outcome onto ask/allow. Errors fail closed on consequential tools. */
export function applyJudgeDecision(input: {
  decision: AutoReviewJudgeDecision;
  consequential: boolean;
}): "ask" | "allow" {
  if (input.decision === "pass") return "allow";
  if (input.decision === "ask") return "ask";
  return input.consequential ? "ask" : "allow";
}

/**
 * The Markdown characters an approval card title escapes in the names it shows, as a regular
 * expression character class. Web renders the title as Markdown; plain-text surfaces unescape them.
 */
export const CARD_TITLE_ESCAPED_CHARACTERS = "[\\\\`*_[\\]<>~|]";

export function isSecretAskBlock(block: {
  kind: string;
  input?: string;
  approvalEffectId?: string;
}): boolean {
  return block.kind === "ask" && block.input === "secret" && !block.approvalEffectId;
}

/** A card the bot showed with a tool and waits on: a secret, a short answer, or files. */
export function isDismissableAskBlock(block: {
  kind: string;
  input?: string;
  approvalEffectId?: string;
}): boolean {
  return (
    block.kind === "ask" &&
    (block.input === "secret" || block.input === "text" || block.input === "file") &&
    !block.approvalEffectId
  );
}

export function isApprovalAskBlock(block: {
  kind: string;
  approvalEffectId?: string;
  actions?: Array<{ id: string; label: string }>;
}): boolean {
  if (block.kind !== "ask" || !block.approvalEffectId || !block.actions?.length) return false;
  const actionIds = new Set(block.actions.map((action) => action.id));
  return (
    actionIds.has("allow") &&
    actionIds.has("deny") &&
    [...actionIds].every((id) => id === "allow" || id === "always" || id === "deny")
  );
}
